import { captureException } from "@sentry/nuxt"
import { FetchError } from "ofetch"

export type UseMutationReturn<TData, TArgs extends unknown[] = []> = {
  mutate: (...args: TArgs) => Promise<TData | undefined>
  loading: Ref<boolean>
  error: Ref<unknown>
  result: Ref<TData | undefined>
}

type BeforeMutationAbort = {
  result: "error"
  reason: string
}

export type BaseMutationOptions = {
  successKey?: MaybeRef<string>
  /**
   * Callback executed before the mutation starts.
   * If this function returns the `BeforeMutationAbort` object, the mutation will be aborted.
   */

  /* eslint-disable @typescript-eslint/no-invalid-void-type */
  beforeMutation?: () =>
    | BeforeMutationAbort
    | void
    | Promise<BeforeMutationAbort | void>
  /* eslint-enable @typescript-eslint/no-invalid-void-type */

  /**
   * Called when the mutation fails.
   * If you provide an `onError` callback, `useMutation` will not automatically report the error to Sentry.
   * You must manually call `captureException(error)` for unhandled errors you want to report.
   */
  onError?: (error: unknown) => Promise<unknown> | unknown
  replaceKey?: string
  clearKeys?: string[]
}

export function useMutation<TData, TArgs extends unknown[] = []>(
  options: BaseMutationOptions & {
    onSuccess?: (result: TData) => Promise<unknown> | unknown
    fetchFunction: (...args: TArgs) => Promise<TData>
  },
): {
  mutate(...args: TArgs): Promise<TData>
  loading: Ref<boolean>
  error: Ref<unknown>
  result: Ref<TData | undefined>
}

export function useMutation(
  options: BaseMutationOptions & {
    onSuccess?: (result: unknown) => Promise<unknown> | unknown
    fetchFunction: (...args: unknown[]) => Promise<unknown>
  },
) {
  const { t } = useI18n()
  const loading = ref(false)
  const error = ref<unknown>()
  const result = ref<unknown>()
  const { displayError, displayNotification } = useNotifications()

  const replaceCachedData = (newData: unknown) => {
    if (!options.replaceKey) {
      return
    }

    const { data } = useNuxtData(options.replaceKey)
    data.value = newData
  }

  async function fetcher(...args: unknown[]) {
    try {
      loading.value = true
      error.value = undefined

      if (options.beforeMutation) {
        const beforeMutationResult = await options.beforeMutation()

        if (beforeMutationResult?.result == "error") {
          loading.value = false
          return
        }
      }

      result.value = await options.fetchFunction(...args)

      if (options.successKey) {
        displayNotification({
          description: t(unref(options.successKey)),
          type: "success",
        })
      }

      if (options.replaceKey) {
        replaceCachedData(result.value)
      }

      if (options.clearKeys) {
        clearNuxtData(options.clearKeys)
      }

      if (options.onSuccess) {
        await options.onSuccess(result.value)
      }

      loading.value = false
      return result.value
    } catch (e) {
      if (options.onError) {
        await options.onError(e)
      } else {
        displayError(e)
        // Skip FetchError since $api already reports these errors to Sentry to avoid duplicate reports.
        if (!(e instanceof FetchError)) {
          captureException(e)
        }
      }
      error.value = e
      loading.value = false
    }
  }
  return { mutate: fetcher, loading, error, result }
}
